import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { setCurrentBrand } from "src/brand/currentBrand";
import { applyBrandI18n } from "src/brand/i18n.runtime";
import { SpinnerFullPage } from "src/core/components/custom-ui/spinner-full-page";
import { BrandId, BRANDS } from "src/core/constants/brand";
import { ROUTE_PATH } from "src/core/constants/routePath";
import { GetFeaturesQuery, useGetFeaturesQuery } from "src/core/gql/graphql";
import { useTenantIdFromRoute } from "src/core/hooks/routing/useTenantIdFromRoute";
import i18n from "src/core/i18n";
import { tenantPaths } from "src/core/utils/route";

type BrandContextValue = {
  brand: BrandId;
};

export const BrandContext = createContext<BrandContextValue | null>(null);

export const useBrand = (): BrandContextValue => {
  const ctx = useContext(BrandContext);
  if (!ctx) {
    throw new Error("useBrand must be used within a BrandProvider");
  }
  return ctx;
};

type CompatFeatures = Partial<Record<keyof typeof BRANDS, boolean | null>>;

const resolveBrand = (data?: GetFeaturesQuery): BrandId | null => {
  const f = (data?.features ?? undefined) as CompatFeatures | undefined;
  if (!f) {
    return null;
  }

  return "EC";
};

const isBrandId = (v: unknown): v is BrandId => v === BRANDS.EC;

/**
 * Lecture de l'override UNIQUEMENT en dev local.
 * - import.meta.env.DEV: true uniquement en `vite dev` / build dev
 * - VITE_BRAND_OVERRIDE: défini seulement dans .env.local (non commité)
 */
const getLocalBrandOverride = (): BrandId | null => {
  if (!import.meta.env.DEV) return null;
  const raw = import.meta.env.VITE_BRAND_OVERRIDE as unknown;
  return isBrandId(raw) ? raw : null;
};

type RedirectKind = "403" | "500";

/**
 * Résout la redirection en fonction du type d'erreur et du tenantId.
 */
const resolveErrorRedirect = (kind: RedirectKind, tenantId: string | null): string => {
  if (!tenantId) {
    console.warn("[NAV] resolveErrorRedirect -> ERROR_403 (no tenantId)");
    return kind === "403" ? ROUTE_PATH.ERROR_403 : ROUTE_PATH.ERROR_500;
  }
  console.warn("[NAV] resolveErrorRedirect -> redirect to error page", {
    kind,
    tenantId,
  });
  return kind === "403" ? tenantPaths.error403(tenantId) : tenantPaths.error500(tenantId);
};

export const BrandProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const brandOverride = getLocalBrandOverride();
  const tenantId = useTenantIdFromRoute();
  const location = useLocation();

  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  const { data, loading } = useGetFeaturesQuery({
    fetchPolicy: "cache-first",
    skip: !!brandOverride,
    context: {
      suppressRedirect: true,
    },
    onError: (error) => {
      // Permission denied => 403
      if (error?.message === "Permission denied") {
        console.warn("[NAV] BrandProvider -> ERROR_403 (permission denied)");
        setRedirectTo(resolveErrorRedirect("403", tenantId));
        return;
      }
      // Toute autre erreur => 500
      setRedirectTo(resolveErrorRedirect("500", tenantId));
    },
  });

  const resolvedBrand = useMemo<BrandId | null>(
    () => brandOverride ?? resolveBrand(data),
    [brandOverride, data]
  );

  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    (async () => {
      if (!resolvedBrand) {
        document.documentElement.removeAttribute("data-brand");
        setI18nReady(true);
        return;
      }

      setCurrentBrand(resolvedBrand);
      document.documentElement.setAttribute("data-brand", resolvedBrand);

      // 1) Première application: overlay + bump pour rafraîchir l'UI
      await applyBrandI18n(resolvedBrand, i18n.language, { bump: true });

      // 2) Ensuite, si la langue change, on ré-applique SANS bump (évite la boucle infinie)
      const onLang = (lng: string) => {
        void applyBrandI18n(resolvedBrand, lng, { bump: false });
      };
      i18n.on("languageChanged", onLang);

      if (!isCancelled) setI18nReady(true);

      return () => {
        i18n.off("languageChanged", onLang);
      };
    })();

    return () => {
      isCancelled = true;
      document.documentElement.removeAttribute("data-brand");
    };
  }, [resolvedBrand]);

  // Redirection vers une route qui sera rendue par ErrorLayout
  if (redirectTo && location.pathname !== redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (loading) return <SpinnerFullPage />;

  // Cas fonctionnel : pas d'erreur réseau, mais aucune brand trouvée
  if (!resolvedBrand) {
    console.warn("[NAV] BrandProvider -> ERROR_403 (no brand found)");
    return <Navigate to={resolveErrorRedirect("403", tenantId)} replace />;
  }

  if (!i18nReady) return <SpinnerFullPage />;

  return <BrandContext.Provider value={{ brand: resolvedBrand }}>{children}</BrandContext.Provider>;
};
